<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { householdsStore } from '../stores/householdsStore';
  import { toastStore } from '../stores/toastStore';
  import { activeHouseholdStore } from '../stores/activeHouseholdStore';
  import type { Household, HouseholdType } from '../api';
  import Home from 'lucide-svelte/icons/home';
  import Plus from 'lucide-svelte/icons/plus';
  import Pencil from 'lucide-svelte/icons/pencil';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import Check from 'lucide-svelte/icons/check';
  import X from 'lucide-svelte/icons/x';
  import { Button, Badge, Modal, SectionHeading } from './ui/index';

  let loading = false;
  let error: string | null = null;

  // Delete confirmation
  let deleteTarget: Household | null = null;

  // Create form
  let newName = '';
  let newType: HouseholdType = 'Privat';

  // Edit form
  let editingHousehold: Household | null = null;
  let editName = '';
  let editType: HouseholdType = 'Privat';

  const householdTypes: { value: HouseholdType; labelKey: string }[] = [
    { value: 'Privat', labelKey: 'household.type.Privat' },
    { value: 'Gewerbe', labelKey: 'household.type.Gewerbe' },
    { value: 'VuV', labelKey: 'household.type.VuV' },
  ];

  onMount(async () => {
    if (!householdsStore.isLoaded) {
      await householdsStore.load();
    }
  });

  async function handleCreate() {
    if (!newName.trim()) return;
    loading = true;
    error = null;

    try {
      await householdsStore.create(newName.trim(), newType);
      newName = '';
      newType = 'Privat';
      toastStore.success($_('pages.settings.members_success'));
    } catch (err) {
      error = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      loading = false;
    }
  }

  function startEdit(h: Household) {
    editingHousehold = h;
    editName = h.name || '';
    editType = h.type;
  }

  function cancelEdit() {
    editingHousehold = null;
    editName = '';
    editType = 'Privat';
  }

  async function handleUpdate() {
    if (!editingHousehold || !editName.trim()) return;
    loading = true;
    error = null;

    try {
      await householdsStore.update(editingHousehold.id, {
        name: editName.trim(),
        type: editType,
      });
      cancelEdit();
      toastStore.success($_('pages.settings.members_success'));
    } catch (err) {
      error = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      loading = false;
    }
  }

  function confirmDelete(h: Household) {
    deleteTarget = h;
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    loading = true;
    error = null;

    try {
      const id = deleteTarget.id;
      await householdsStore.delete(id);
      if ($activeHouseholdStore === id) {
        activeHouseholdStore.reset();
      }
      deleteTarget = null;
      toastStore.success($_('pages.settings.members_success'));
    } catch (err) {
      error = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      loading = false;
    }
  }

  function isGlobal(h: Household): boolean {
    return h.id === 'Global';
  }
</script>

<SectionHeading icon={Home} title={$_('household.management_title')}>
  <p class="text-xs text-text-muted mb-4">{$_('household.management_description')}</p>
</SectionHeading>

<!-- Household List -->
<div class="flex flex-col gap-2 mb-4">
  {#each $householdsStore as h (h.id)}
    <div
      data-testid="household-row-{h.id}"
      class="flex justify-between items-center px-3 py-2.5 bg-bg-alt rounded"
    >
      {#if editingHousehold?.id === h.id}
        <div class="flex flex-wrap gap-2 w-full items-center">
          <input
            type="text"
            bind:value={editName}
            placeholder={$_('household.name_label')}
            class="flex-1 min-w-[120px] px-3 py-1.5 bg-surface border border-border rounded text-sm text-text focus:outline-none focus:border-accent"
          />
          <select
            bind:value={editType}
            class="px-3 py-1.5 bg-surface border border-border rounded text-sm text-text"
          >
            {#each householdTypes as ht (ht.value)}
              <option value={ht.value}>{$_(ht.labelKey)}</option>
            {/each}
          </select>
          <div class="flex gap-1.5">
            <Button
              variant="primary"
              size="sm"
              disabled={loading || !editName.trim()}
              on:click={handleUpdate}
            >
              <Check class="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
            <Button variant="secondary" size="sm" disabled={loading} on:click={cancelEdit}>
              <X class="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      {:else}
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-text">{h.name || h.id}</span>
          <Badge variant="default">{$_(`household.type.${h.type}`)}</Badge>
          {#if isGlobal(h)}
            <Badge variant="info">{$_('household.default_badge')}</Badge>
          {/if}
        </div>
        <div class="flex gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            disabled={loading}
            on:click={() => startEdit(h)}
          >
            <Pencil class="w-3.5 h-3.5" strokeWidth={1.5} />
          </Button>
          {#if !isGlobal(h)}
            <Button
              variant="danger"
              size="sm"
              iconOnly
              disabled={loading}
              on:click={() => confirmDelete(h)}
              data-testid="household-delete-button-{h.id}"
            >
              <Trash2 class="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
          {/if}
        </div>
      {/if}
    </div>
  {:else}
    <p class="text-sm text-text-muted text-center py-3 bg-bg-alt rounded">
      {$_('messages.loading')}
    </p>
  {/each}
</div>

<!-- Create Form -->
<div class="pt-3 border-t border-border">
  <h3 class="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
    {$_('household.create_title')}
  </h3>
  <div class="flex gap-2 flex-wrap">
    <input
      data-testid="household-name-input"
      type="text"
      bind:value={newName}
      placeholder={$_('household.name_label')}
      class="flex-1 min-w-[150px] px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
    />
    <select
      data-testid="household-type-select"
      bind:value={newType}
      class="px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text"
    >
      {#each householdTypes as ht (ht.value)}
        <option value={ht.value}>{$_(ht.labelKey)}</option>
      {/each}
    </select>
    <Button
      variant="primary"
      size="sm"
      disabled={loading || !newName.trim()}
      on:click={handleCreate}
      data-testid="household-create-button"
    >
      <Plus class="w-4 h-4" strokeWidth={1.5} />
      {$_('household.create_button')}
    </Button>
  </div>
</div>

<!-- Messages -->
{#if error}
  <div class="mt-3 px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error">
    {error}
  </div>
{/if}

<!-- Delete Confirmation Modal -->
<Modal
  open={deleteTarget !== null}
  title={$_('household.delete_confirm_title')}
  on:close={() => (deleteTarget = null)}
>
  <p class="text-sm text-text-secondary mb-2">
    {$_('household.delete_confirm')}
  </p>
  {#if deleteTarget}
    <p class="text-sm font-medium text-text">{deleteTarget.name || deleteTarget.id}</p>
  {/if}
  <p class="text-sm text-warning mt-2">
    {$_('household.delete_migration_warning')}
  </p>
  <svelte:fragment slot="footer">
    <Button variant="danger" disabled={loading} on:click={handleDelete}>
      {loading ? $_('messages.deleting') : $_('household.delete_button')}
    </Button>
    <Button variant="secondary" disabled={loading} on:click={() => (deleteTarget = null)}>
      {$_('pages.settings.cancel')}
    </Button>
  </svelte:fragment>
</Modal>
